import { useSearchParams } from 'react-router-dom';
import { Button } from '../ui/button';
import {
  QUICK_PICKS,
  resolveQuickPick,
  detectActiveQuickPick,
  type QuickPick,
} from '@/lib/date-range';

export interface DateRangeChange {
  from: string | null;
  to: string | null;
  all: boolean;
}

/**
 * Quick-pick + custom Từ/Đến date range. Reads current state from the URL
 * (`date_from`/`date_to`/`range`). The caller owns URL writes via `onChange`
 * (so transactions can also set `page=1`, dashboard doesn't).
 *
 * An empty URL maps to `defaultPick`. Selecting "Tất cả" emits `all:true` only
 * when "all" is NOT the default (dashboard), so the all-time choice is
 * distinguishable from the default; for transactions (`defaultPick="all"`) it
 * just clears the dates.
 */
export function DateRangeFilter({
  defaultPick,
  onChange,
}: {
  defaultPick: QuickPick;
  onChange: (next: DateRangeChange) => void;
}) {
  const [searchParams] = useSearchParams();
  const dateFrom = searchParams.get('date_from') ?? '';
  const dateTo = searchParams.get('date_to') ?? '';
  const isAll = searchParams.get('range') === 'all';

  const activePick: QuickPick | null = isAll
    ? 'all'
    : dateFrom || dateTo
      ? detectActiveQuickPick(dateFrom, dateTo)
      : defaultPick;

  function pick(label: QuickPick) {
    const { from, to } = resolveQuickPick(label);
    if (from === null && to === null) {
      onChange({ from: null, to: null, all: label !== defaultPick });
    } else {
      onChange({ from, to, all: false });
    }
  }

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
      {QUICK_PICKS.map(({ label, display }) => (
        <Button
          key={label}
          variant={activePick === label ? 'default' : 'secondary'}
          onClick={() => pick(label)}
        >
          {display}
        </Button>
      ))}
      <div className="hidden h-5 w-px bg-border sm:block" />
      <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <span>Từ</span>
        <input
          type="date"
          value={dateFrom}
          onChange={(e) =>
            onChange({ from: e.target.value || null, to: dateTo || null, all: false })
          }
          className="rounded border border-border bg-background px-2 py-1 text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
        />
      </label>
      <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <span>Đến</span>
        <input
          type="date"
          value={dateTo}
          onChange={(e) =>
            onChange({ from: dateFrom || null, to: e.target.value || null, all: false })
          }
          className="rounded border border-border bg-background px-2 py-1 text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
        />
      </label>
    </div>
  );
}
